import type { RequestContext } from "../context/request-context.js";
import type { AIReviewSubjectType } from "../../domain/ai-review/ai-review-model.js";
import { EMPTY_FACTS, type ReviewFacts, type ReviewField } from "../../domain/ai-review/technical-rules.js";
import { PermissionCode } from "../../permissions/permissions.js";
import type { PermissionCode as PermissionCodeValue } from "../../domain/identity/access-types.js";
import { isMeClosed, meEffectiveThickness, validateMeItems, type MeReport } from "../../domain/engineering/me-report-domain.js";
import { resolveFinalLaudo } from "../../domain/engineering/me-measurements.js";
import { validateLpReportCompleteness, type LpReport } from "../../domain/ndt/lp-report-domain.js";
import { validatePmReportCompleteness, type PmReport } from "../../domain/ndt/pm-report-domain.js";
import { validateConventionalUsReportCompleteness, type ConventionalUsReport } from "../../domain/ndt/conventional-us-report-domain.js";
import { validateBscanReportCompleteness, type BscanReport } from "../../domain/ndt/bscan-report-domain.js";
import { validateAvrCompleteness, type AvrReport } from "../../domain/engineering/avr-report-domain.js";

/**
 * ADAPTADORES DA REVISÃO IA — um por tipo de documento, todos no MESMO
 * orquestrador. O adaptador só DESCREVE o documento (campos de texto com
 * `fieldPath`, fatos estruturados, pendências do validador oficial) e GRAVA
 * pelo serviço do próprio módulo — com as travas, validações e auditoria
 * dele. Nenhuma regra de módulo é reescrita aqui.
 */
export interface AIReviewSubject {
  readonly type: AIReviewSubjectType;
  readonly reportId: string;
  readonly reportNumber: string;
  readonly revision: string;
  readonly clientId: string;
  readonly projectId: string | null;
  readonly fields: readonly ReviewField[];
  readonly facts: ReviewFacts;
  readonly modulePendencies: readonly string[];
  /** Documento fechado/aprovado não recebe texto — só leitura da revisão. */
  readonly editable: boolean;
  readonly procedureCodes: readonly string[];
  readonly normCodes: readonly string[];
}

export interface AIReviewSubjectAdapter {
  readonly type: AIReviewSubjectType;
  /** Permissão de edição do MÓDULO — aplicar texto exige esta e a do INSPECT IA REVIEW. */
  readonly editPermission: PermissionCodeValue;
  readonly readPermission: PermissionCodeValue;
  load(context: RequestContext, reportId: string): Promise<AIReviewSubject | null>;
  writeField(context: RequestContext, reportId: string, fieldPath: string, text: string): Promise<void>;
  /** Documentos do tipo, para escolher o que revisar (pela listagem do próprio módulo). */
  list(context: RequestContext): Promise<ReviewableItem[]>;
}

export interface ReviewableItem {
  readonly reportId: string;
  readonly reportNumber: string;
  readonly label: string;
  readonly status: string;
}

// ═══════════════════════════════════════════════════════════ caminhos de campo

type Segment = { name: string } | { index: number } | { key: string };

/** "results[key=PT-1].description" · "conclusion.observation" · "points[2].location". */
export function parseFieldPath(path: string): Segment[] {
  const out: Segment[] = [];
  for (const part of path.split(".")) {
    const m = part.match(/^([A-Za-z_$][\w$]*)((?:\[[^\]]+\])*)$/);
    if (!m) throw new Error(`Caminho de campo inválido: ${path}`);
    out.push({ name: m[1]! });
    for (const b of m[2]!.matchAll(/\[([^\]]+)\]/g)) {
      const inner = b[1]!;
      if (/^\d+$/.test(inner)) out.push({ index: Number(inner) });
      else if (inner.startsWith("key=")) out.push({ key: inner.slice(4) });
      else throw new Error(`Caminho de campo inválido: ${path}`);
    }
  }
  return out;
}

function step(target: unknown, seg: Segment): unknown {
  if (target === null || target === undefined) return undefined;
  if ("name" in seg) return (target as Record<string, unknown>)[seg.name];
  if (!Array.isArray(target)) return undefined;
  if ("index" in seg) return target[seg.index];
  return target.find(item => item && (item as { key?: unknown }).key === seg.key);
}

export function getFieldText(obj: unknown, path: string): string | undefined {
  let cur: unknown = obj;
  for (const seg of parseFieldPath(path)) cur = step(cur, seg);
  return typeof cur === "string" ? cur : undefined;
}

export function setFieldText(obj: unknown, path: string, text: string): void {
  const segs = parseFieldPath(path);
  let cur: unknown = obj;
  for (const seg of segs.slice(0, -1)) cur = step(cur, seg);
  const last = segs[segs.length - 1]!;
  if (!cur || typeof cur !== "object" || !("name" in last) || typeof (cur as Record<string, unknown>)[last.name] !== "string") {
    throw new Error(`Campo ${path} não encontrado no documento.`);
  }
  (cur as Record<string, unknown>)[last.name] = text;
}

// ═══════════════════════════════════════════════════════════ fábrica genérica

interface ReportService<R> {
  getReport(context: RequestContext, id: string): Promise<R | null>;
  saveDraft(context: RequestContext, report: R): Promise<R>;
  listByClient?(context: RequestContext, clientId?: string): Promise<readonly R[]>;
  listForEngineering?(context: RequestContext, clientId?: string | null): Promise<readonly R[]>;
}

interface Spec<R> {
  readonly type: AIReviewSubjectType;
  readonly editPermission: PermissionCodeValue;
  readonly readPermission: PermissionCodeValue;
  readonly service: ReportService<R>;
  readonly describe: (r: R) => Omit<AIReviewSubject, "type" | "reportId">;
}

const text = (v: unknown) => (typeof v === "string" ? v : "");
const field = (fieldPath: string, label: string, value: unknown, extra: Partial<ReviewField> = {}): ReviewField => ({ fieldPath, label, text: text(value), writable: true, ...extra });
const refs = (...values: unknown[]) => values.map(text).filter(v => v.trim());
const verdictOf = (laudo: string): ReviewFacts["results"][number]["verdict"] =>
  laudo === "A" || laudo === "APROVADO" ? "APPROVED" : laudo === "R" || laudo === "REPROVADO" ? "REJECTED" : laudo === "P" || laudo === "EM_ELABORACAO" || laudo === "" ? "PENDING" : "OTHER";
const CLOSED = new Set(["FINALIZED", "SIGNED"]);

function serviceAdapter<R extends { id: string }>(spec: Spec<R>): AIReviewSubjectAdapter {
  return {
    type: spec.type,
    editPermission: spec.editPermission,
    readPermission: spec.readPermission,
    async load(context, reportId) {
      const r = await spec.service.getReport(context, reportId);
      return r ? { type: spec.type, reportId: String(r.id), ...spec.describe(r) } : null;
    },
    async writeField(context, reportId, fieldPath, value) {
      const r = await spec.service.getReport(context, reportId);
      if (!r) throw new Error("Documento não encontrado.");
      setFieldText(r, fieldPath, value);
      await spec.service.saveDraft(context, r);
    },
    async list(context) {
      const svc = spec.service;
      const rows = svc.listByClient ? await svc.listByClient(context, context.activeClientId ? String(context.activeClientId) : undefined)
        : svc.listForEngineering ? await svc.listForEngineering(context, context.activeClientId ? String(context.activeClientId) : null) : [];
      return rows.map(r => {
        const d = spec.describe(r);
        const row = r as unknown as Record<string, unknown>;
        return { reportId: String(r.id), reportNumber: d.reportNumber, label: [d.reportNumber, text(row["equipmentTag"])].filter(Boolean).join(" — "), status: text(row["status"]) };
      });
    }
  };
}

export interface AIReviewAdapterDeps {
  meReportService?: ReportService<MeReport>;
  lpReportService?: ReportService<LpReport>;
  pmReportService?: ReportService<PmReport>;
  conventionalUsReportService?: ReportService<ConventionalUsReport>;
  bscanReportService?: ReportService<BscanReport>;
  avrReportService?: ReportService<AvrReport>;
  rdoService?: {
    getRdo(context: RequestContext, id: string): Promise<Record<string, unknown> | null>;
    updateRdo(context: RequestContext, id: string, req: Record<string, unknown>): Promise<unknown>;
    listRdos(context: RequestContext, filters?: Record<string, unknown>): Promise<ReadonlyArray<Record<string, unknown>>>;
  };
  reportService?: {
    getReportById(context: RequestContext, id: string): Promise<Record<string, unknown> | null>;
    updateReport(context: RequestContext, report: Record<string, unknown>): Promise<void>;
    listRecommendationsByReport(context: RequestContext, id: string): Promise<ReadonlyArray<Record<string, unknown>>>;
    listReports(context: RequestContext): Promise<ReadonlyArray<Record<string, unknown>>>;
  };
}

/** Todos os adaptadores disponíveis na composição (um serviço ausente = tipo não oferecido). */
export function buildAIReviewAdapters(deps: AIReviewAdapterDeps): AIReviewSubjectAdapter[] {
  const out: AIReviewSubjectAdapter[] = [];

  if (deps.meReportService) out.push(serviceAdapter<MeReport>({
    type: "ME", editPermission: PermissionCode.MeReportEdit, readPermission: PermissionCode.MeReportView, service: deps.meReportService,
    describe: r => ({
      reportNumber: r.reportNumber, revision: r.revision, clientId: String(r.clientId), projectId: r.projectId,
      fields: [
        field("comments", "Comentários e Observações", r.comments, { conclusive: true }),
        ...(r.measurementTechniqueOther ? [field("measurementTechniqueOther", "Técnica de Medição (descrição)", r.measurementTechniqueOther)] : [])
      ],
      facts: {
        ...EMPTY_FACTS,
        measurements: (r.points || []).map(p => ({ label: `Ponto ${p.id || p.key}`, value: meEffectiveThickness(r, p), min: p.minThickness })),
        results: (r.points || []).map(p => ({ label: `Ponto ${p.id || p.key}`, verdict: verdictOf(String(resolveFinalLaudo(p.minThickness, meEffectiveThickness(r, p), p.laudoOverride as never) ?? "")) })),
        declaredReferences: refs(r.procedureRevision, r.referenceNorm, r.acceptanceCriteria),
        dates: r.testDate ? [{ label: "Data do ensaio", value: r.testDate }] : [],
        periods: r.periodStart && r.periodEnd ? [{ label: "Período do ensaio", start: r.periodStart, end: r.periodEnd }] : []
      },
      modulePendencies: validateMeItems(r).pendencies.map(p => p.message),
      editable: !isMeClosed(r.status),
      procedureCodes: refs(r.procedureRevision), normCodes: refs(r.referenceNorm)
    })
  }));

  if (deps.lpReportService) out.push(serviceAdapter<LpReport>({
    type: "LP", editPermission: PermissionCode.LpReportEdit, readPermission: PermissionCode.LpReportView, service: deps.lpReportService,
    describe: r => {
      const rr = r as LpReport & Record<string, unknown>;
      return {
        reportNumber: text(rr["reportNumber"]), revision: text(rr["revision"]), clientId: String(rr["clientId"]), projectId: (rr["projectId"] as string | null) ?? null,
        fields: [
          field("observations", "Observações", r.observations, { conclusive: true }),
          ...(r.results || []).map((it, i) => field(`results[key=${it.key}].description`, `Resultado ${it.item || i + 1} — descrição`, it.description))
        ],
        facts: { ...EMPTY_FACTS, results: (r.results || []).map((it, i) => ({ label: `Item ${it.item || i + 1}`, verdict: verdictOf(it.laudo) })), declaredReferences: refs(rr["procedureRevision"], rr["referenceNorm"], rr["acceptanceCriteria"]), dates: rr["testDate"] ? [{ label: "Data do ensaio", value: text(rr["testDate"]) }] : [] },
        modulePendencies: validateLpReportCompleteness(r).errors,
        editable: !CLOSED.has(text(rr["status"])),
        procedureCodes: refs(rr["procedureRevision"]), normCodes: refs(rr["referenceNorm"])
      };
    }
  }));

  const ndtCommon = (r: PmReport | ConventionalUsReport | BscanReport) => ({
    reportNumber: text((r as unknown as Record<string, unknown>)["reportNumber"]), revision: text((r as unknown as Record<string, unknown>)["revision"]),
    clientId: String((r as unknown as Record<string, unknown>)["clientId"]), projectId: r.projectId,
    declaredReferences: refs(r.procedureRevision, r.referenceNorm, r.acceptanceCriteria),
    dates: r.testDate ? [{ label: "Data do ensaio", value: r.testDate }] : [],
    editable: !CLOSED.has(text((r as unknown as Record<string, unknown>)["status"])),
    procedureCodes: refs(r.procedureRevision), normCodes: refs(r.referenceNorm)
  });

  if (deps.pmReportService) out.push(serviceAdapter<PmReport>({
    type: "PM", editPermission: PermissionCode.PmReportEdit, readPermission: PermissionCode.PmReportView, service: deps.pmReportService,
    describe: r => {
      const c = ndtCommon(r);
      return {
        reportNumber: c.reportNumber, revision: c.revision, clientId: c.clientId, projectId: c.projectId, editable: c.editable, procedureCodes: c.procedureCodes, normCodes: c.normCodes,
        fields: [
          field("observations", "Observações", r.observations, { conclusive: true }),
          ...(r.technicalNote ? [field("technicalNote", "Nota técnica", r.technicalNote)] : []),
          ...(r.functionalCheckNote ? [field("functionalCheckNote", "Verificação funcional — nota", r.functionalCheckNote)] : []),
          ...(r.results || []).map((it, i) => field(`results[key=${it.key}].findings`, `Resultado ${it.item || i + 1} — indicações`, it.findings))
        ],
        facts: { ...EMPTY_FACTS, results: (r.results || []).map((it, i) => ({ label: `Item ${it.item || i + 1}`, verdict: verdictOf(it.laudo) })), declaredReferences: c.declaredReferences, dates: c.dates },
        modulePendencies: validatePmReportCompleteness(r).errors
      };
    }
  }));

  if (deps.conventionalUsReportService) out.push(serviceAdapter<ConventionalUsReport>({
    type: "USC", editPermission: PermissionCode.UscReportEdit, readPermission: PermissionCode.UscReportView, service: deps.conventionalUsReportService,
    describe: r => {
      const c = ndtCommon(r);
      return {
        reportNumber: c.reportNumber, revision: c.revision, clientId: c.clientId, projectId: c.projectId, editable: c.editable, procedureCodes: c.procedureCodes, normCodes: c.normCodes,
        fields: [
          field("observations", "Observações", r.observations, { conclusive: true }),
          ...(r.calibrationNote ? [field("calibrationNote", "Calibração — nota", r.calibrationNote)] : []),
          ...(r.results || []).map((it, i) => field(`results[key=${it.key}].indication`, `Resultado ${it.item || i + 1} — indicação`, it.indication))
        ],
        facts: { ...EMPTY_FACTS, results: (r.results || []).map((it, i) => ({ label: `Item ${it.item || i + 1}`, verdict: verdictOf(it.laudo) })), declaredReferences: c.declaredReferences, dates: c.dates },
        modulePendencies: validateConventionalUsReportCompleteness(r).errors
      };
    }
  }));

  if (deps.bscanReportService) out.push(serviceAdapter<BscanReport>({
    type: "BSCAN", editPermission: PermissionCode.BscanReportEdit, readPermission: PermissionCode.BscanReportView, service: deps.bscanReportService,
    describe: r => {
      const c = ndtCommon(r);
      return {
        reportNumber: c.reportNumber, revision: c.revision, clientId: c.clientId, projectId: c.projectId, editable: c.editable, procedureCodes: c.procedureCodes, normCodes: c.normCodes,
        fields: [
          field("objective", "Objetivo", r.objective),
          field("observations", "Observações", r.observations, { conclusive: true }),
          ...(r.results || []).map((it, i) => field(`results[key=${it.key}].observation`, `Resultado ${it.item || i + 1} — observação`, it.observation)),
          ...(r.photos || []).filter(p => p.caption).map((p, i) => field(`photos[key=${p.key}].caption`, `Foto ${i + 1} — legenda`, p.caption))
        ],
        facts: { ...EMPTY_FACTS, results: (r.results || []).map((it, i) => ({ label: `Área ${it.item || i + 1}`, verdict: verdictOf(it.laudo) })), declaredReferences: c.declaredReferences, dates: c.dates },
        modulePendencies: validateBscanReportCompleteness(r).errors
      };
    }
  }));

  if (deps.avrReportService) out.push(serviceAdapter<AvrReport>({
    type: "AVR", editPermission: PermissionCode.AvrUpdate, readPermission: PermissionCode.AvrRead, service: deps.avrReportService,
    describe: r => ({
      reportNumber: r.avrNumber, revision: r.revision, clientId: String(r.clientId), projectId: r.projectId,
      fields: [
        field("analysis", "Análise", r.analysis),
        field("conclusion.observation", "Conclusão — observação", r.conclusion?.observation, { conclusive: true })
      ],
      facts: {
        ...EMPTY_FACTS,
        measurements: (r.components || []).map(c => {
          const last = [...(c.readings || [])].sort((a, b) => String(b.date).localeCompare(String(a.date)))[0];
          return { label: `Ponto ${c.pointId}`, value: last ? last.thicknessMm : null, min: c.minimumThicknessMm };
        }),
        results: r.conclusion?.status ? [{ label: "Status geral da AVR", verdict: r.conclusion.status === "REPROVADA" ? "REJECTED" : r.conclusion.status === "APROVADA" ? "APPROVED" : "OTHER" }] : [],
        recommendationsCount: (r.recommendationIds || []).length,
        declaredReferences: ["API 570"]
      },
      modulePendencies: validateAvrCompleteness(r).errors,
      editable: !["PLH_APPROVED", "SIGNED", "FINALIZED"].includes(String(r.status)),
      procedureCodes: [], normCodes: ["API 570"]
    })
  }));

  if (deps.rdoService) {
    const rdo = deps.rdoService;
    out.push({
      type: "RDO", editPermission: PermissionCode.RdoUpdate, readPermission: PermissionCode.RdoRead,
      async load(context, reportId) {
        const r = await rdo.getRdo(context, reportId);
        if (!r) return null;
        return {
          type: "RDO", reportId: String(r["id"]), reportNumber: text(r["date"]) ? `RDO ${text(r["date"])}` : "RDO", revision: String(r["version"] ?? ""),
          clientId: String(r["clientId"] ?? ""), projectId: (r["projectId"] as string | null) ?? null,
          fields: [field("description", "Descrição do Serviço", r["description"]), ...(r["inspectorComment"] ? [field("inspectorComment", "Comentário do Inspetor", r["inspectorComment"])] : [])],
          facts: { ...EMPTY_FACTS, dates: text(r["date"]) ? [{ label: "Data do RDO", value: text(r["date"]) }] : [] },
          modulePendencies: [],
          editable: !["ISSUED", "CANCELLED"].includes(String(r["status"])),
          procedureCodes: [], normCodes: []
        };
      },
      async writeField(context, reportId, fieldPath, value) {
        const r = await rdo.getRdo(context, reportId);
        if (!r) throw new Error("RDO não encontrado.");
        if (fieldPath !== "description" && fieldPath !== "inspectorComment") throw new Error(`Campo ${fieldPath} não é editável pela revisão.`);
        // Mesmo caminho da tela: updateRdo, com todos os demais campos como estão.
        await rdo.updateRdo(context, reportId, { ...r, [fieldPath]: value });
      },
      async list(context) {
        return (await rdo.listRdos(context, {})).map(r => ({ reportId: String(r["id"]), reportNumber: `RDO ${text(r["date"])}`, label: `RDO ${text(r["date"])}${r["functionName"] ? ` — ${text(r["functionName"])}` : ""}`, status: text(r["status"]) }));
      }
    });
  }

  if (deps.reportService) {
    const ri = deps.reportService;
    const RI_FIELDS: Array<[string, string, boolean]> = [
      ["objective", "Objetivo", false], ["inspectionObservations", "Observações da inspeção", false], ["maintenanceServices", "Serviços de manutenção", false],
      ["damageMechanisms", "Mecanismos de dano", false], ["conclusion", "Conclusão", true]
    ];
    out.push({
      type: "INTEGRITY", editPermission: PermissionCode.ReportUpdate, readPermission: PermissionCode.ReportRead,
      async load(context, reportId) {
        const r = await ri.getReportById(context, reportId);
        if (!r) return null;
        const recs = await ri.listRecommendationsByReport(context, reportId).catch(() => [] as ReadonlyArray<Record<string, unknown>>);
        return {
          type: "INTEGRITY", reportId: String(r["id"]), reportNumber: text(r["reportNumber"]), revision: String(r["version"] ?? ""),
          clientId: String(r["clientId"] ?? ""), projectId: (r["projectId"] as string | null) ?? null,
          fields: [
            ...RI_FIELDS.map(([p, l, c]) => field(p, l, r[p], { conclusive: c })),
            // Recomendações: revisadas, mas corrigidas no próprio módulo (numeração ZR, versão e rastreabilidade são dele).
            ...recs.map(rec => field(`recommendations[key=${String(rec["id"])}].scope`, `Recomendação ${text(rec["zr"]) || text(rec["id"])}`, rec["scope"], { writable: false }))
          ],
          facts: { ...EMPTY_FACTS, recommendationsCount: recs.length },
          modulePendencies: [],
          editable: !r["finalization"] && !["FINALIZED", "FINALIZED_BY_INSPECTOR", "SUBMITTED_TO_ENGINEERING", "UNDER_ENGINEERING_REVIEW"].includes(String(r["status"])),
          procedureCodes: [], normCodes: []
        };
      },
      async writeField(context, reportId, fieldPath, value) {
        if (!RI_FIELDS.some(([p]) => p === fieldPath)) throw new Error("Recomendações são corrigidas no próprio Relatório de Integridade.");
        const r = await ri.getReportById(context, reportId);
        if (!r) throw new Error("Relatório de Integridade não encontrado.");
        await ri.updateReport(context, { ...r, [fieldPath]: value });
      },
      async list(context) {
        return (await ri.listReports(context)).map(r => ({ reportId: String(r["id"]), reportNumber: text(r["reportNumber"]), label: text(r["reportNumber"]) || String(r["id"]), status: text(r["status"]) }));
      }
    });
  }

  return out;
}
